import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Ticket, Trophy, Sparkles, ArrowRight, Star } from "lucide-react";
import api from "../lib/api";
import { NeonButton, NeonCard, NeonPrice, SectionTitle, Spinner } from "../components/Neon";
import { LaunchCountdown } from "../components/LaunchCountdown";
import { LaunchDraws } from "../components/LaunchDraws";
import { CompetitionCard } from "../components/CompetitionCard";
import { gbp } from "../lib/format";

export default function Home() {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/competitions").then(({ data }) => setComps(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = comps.find((c) => c.featured) || comps[0];
  const topPrize = comps.reduce((m, c) => Math.max(m, c.prize_value || 0), 0);

  return (
    <div>
      {/* Launch countdown — top, most prominent */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
        <LaunchCountdown />
      </section>

      {/* Launch Day Special Draws (enterable before go-live) */}
      <LaunchDraws />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 80% 10%, rgba(255,45,149,.25), transparent), radial-gradient(50% 50% at 10% 30%, rgba(0,229,255,.18), transparent)" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(0,229,255,.4)] px-3 py-1 text-xs uppercase tracking-widest text-[var(--neon-cyan)]"><Sparkles className="h-3.5 w-3.5" /> UK Luxury Prize Competitions</span>
            <h1 className="mt-4 font-display uppercase leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-white glow-white">
              Win <span className="glow-pink">Supercars</span>, <span className="glow-cyan">Watches</span> &amp; <span className="glow-green">Cash</span>
            </h1>
            <p className="mt-5 text-[var(--text-muted)] max-w-xl text-base sm:text-lg">Answer a simple skill question, grab your tickets and you could be our next big winner. Up to {gbp(topPrize)} in live prizes right now.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <NeonButton as={Link} to="/competitions" variant="pink" data-testid="hero-enter-cta">Enter Competitions <ArrowRight className="h-4 w-4" /></NeonButton>
              <NeonButton as={Link} to="/members" variant="outlineGreen" data-testid="hero-members-cta">Members Club</NeonButton>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-[var(--text-muted)]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--neon-green)]" /> 18+ &amp; Responsible</span>
              <span className="flex items-center gap-2"><Ticket className="h-4 w-4 text-[var(--neon-cyan)]" /> Free postal entry</span>
            </div>
          </div>
          <div className="lg:justify-self-end w-full">
            {featured ? (
              <Link to={`/competitions/${featured.id}`} className="block neon-flow">
                <div className="neon-inner p-0 overflow-hidden">
                  <div className="relative aspect-[16/11]">
                    <img src={featured.hero_image} alt={featured.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-xs uppercase tracking-widest text-[var(--neon-pink)]">Featured · Prize worth {gbp(featured.prize_value)}</p>
                      <h3 className="font-display text-3xl sm:text-4xl text-white glow-white">{featured.title}</h3>
                      <p className="mt-1 text-sm text-[var(--text-muted)]">From <span className="glow-green font-display text-lg">{gbp(featured.ticket_price)}</span> per ticket</p>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="aspect-[16/11] rounded-2xl border border-white/10" />
            )}
          </div>
        </div>
      </section>

      {/* Live competitions */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between gap-4">
          <SectionTitle accent="white">Live Competitions</SectionTitle>
          <Link to="/competitions" className="text-sm text-[var(--neon-cyan)] hover:underline whitespace-nowrap">View all</Link>
        </div>
        {loading ? <Spinner /> : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {comps.slice(0, 6).map((c) => <CompetitionCard key={c.id} comp={c} />)}
          </div>
        )}
      </section>

      {/* Members teaser */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <NeonCard variant="green" innerClassName="p-8 sm:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <p className="font-display uppercase tracking-widest text-[var(--neon-green)] text-sm">Members Club</p>
            <h2 className="mt-2 font-display uppercase text-4xl text-white">Auto-entry into every monthly <NeonPrice value="£5,000" color="pink" className="text-4xl" /> draw</h2>
            <p className="mt-3 text-[var(--text-muted)]">10 free tickets every cycle into selected competitions. Cancel anytime.</p>
          </div>
          <div className="md:justify-self-end text-center">
            <NeonPrice value="£9.99" color="green" className="text-6xl" /> <span className="text-[var(--text-muted)]">/ month</span>
            <div className="mt-4"><NeonButton as={Link} to="/members" variant="pink" data-testid="members-teaser-cta">Become a Member</NeonButton></div>
          </div>
        </NeonCard>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <SectionTitle accent="cyan">How It Works</SectionTitle>
        <div className="mt-6 grid sm:grid-cols-3 gap-5">
          {[{ i: Trophy, t: "Pick a Competition", d: "Browse live luxury prizes — cars, watches and tax-free cash." }, { i: Star, t: "Answer & Enter", d: "Answer one skill question and choose how many tickets you want." }, { i: Ticket, t: "Win Big", d: "Winners are drawn live. Take the prize or the cash alternative." }].map((s, idx) => (
            <NeonCard key={idx} variant="flow" innerClassName="p-6">
              <s.i className="h-8 w-8 text-[var(--neon-pink)]" />
              <p className="mt-3 font-display uppercase text-2xl text-white">{s.t}</p>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{s.d}</p>
            </NeonCard>
          ))}
        </div>
      </section>
    </div>
  );
}
